import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import { setCurrentStep, setCurrentSubStep } from '../store/rfqSlice';
import { calculateWorkflowStep } from '../utils/workflowUtils';
import { WizardPos, wizardStepPath } from '../utils/wizardRoutes';
import { RFQ } from '../types';

/**
 * Makes the URL the single source of truth for the wizard position.
 *
 * - A step URL restores exactly that step on refresh or deep link, gated once
 *   per RFQ load so nobody lands beyond the workflow's real progress.
 * - Bare `/rfqs/:id` resolves to the RFQ's current step (replace, no history).
 * - Step components still signal position through the store; those changes are
 *   translated into navigations, so browser back/forward walk real steps.
 *
 * Effect order matters: the URL→store sync is declared before the intent
 * sync, so within one commit the store mirrors the address bar before store
 * changes are interpreted as navigation intents.
 */
export const useWizardRoute = (rfq: RFQ | null, rfqId: string, routePos?: WizardPos): WizardPos => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storeStep = useSelector((s: RootState) => s.rfq.currentStep);
  const storeSubStep = useSelector((s: RootState) => s.rfq.currentSubStep);

  const canonicalId = rfq?.rfqId || rfqId;
  const reached = rfq ? calculateWorkflowStep(rfq) : null;

  // URL → store: the store mirrors the address bar.
  const syncedRef = useRef<WizardPos | null>(null);
  useEffect(() => {
    if (!routePos) return;
    syncedRef.current = routePos;
    if (storeStep !== routePos.step) dispatch(setCurrentStep(routePos.step));
    if (storeSubStep !== routePos.subStep) dispatch(setCurrentSubStep(routePos.subStep));
    // Store values are read fresh enough here — re-dispatching is idempotent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePos?.step, routePos?.subStep, dispatch]);

  // Store intents (a step component advancing/retreating) → URL. The first
  // Intent means the store VALUE changed after mount — comparing against the
  // previous value, not "not the first run": StrictMode re-invokes effects
  // with the same closure, so a first-run-only guard let the second pass read
  // the still-initial store and yank a refreshed step URL back to step 1.
  const prevStoreRef = useRef<WizardPos | null>(null);
  useEffect(() => {
    const prev = prevStoreRef.current;
    prevStoreRef.current = { step: storeStep, subStep: storeSubStep };
    if (!prev || (prev.step === storeStep && prev.subStep === storeSubStep)) return;
    const synced = syncedRef.current;
    if (!synced) return;
    if (storeStep !== synced.step || storeSubStep !== synced.subStep) {
      navigate(wizardStepPath(canonicalId, { step: storeStep, subStep: storeSubStep }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeStep, storeSubStep]);

  // Bare /rfqs/:id → the RFQ's current step, once it is known.
  useEffect(() => {
    if (!routePos && rfq && reached) {
      navigate(wizardStepPath(canonicalId, reached), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePos, rfq]);

  // Deep-link gate, once per RFQ load: a URL beyond real progress redirects
  // to the step the RFQ is actually at — honest, never a blank screen.
  // Earlier steps stay reachable for review, matching in-app navigation.
  const gatedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!routePos || !rfq || !reached) return;
    if (gatedForRef.current === canonicalId) return;
    gatedForRef.current = canonicalId;
    if (routePos.step > reached.step) {
      navigate(wizardStepPath(canonicalId, reached), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePos, rfq]);

  return routePos ?? reached ?? { step: 1, subStep: 1 };
};

export default useWizardRoute;
